// Figma: 6086:28468 — see stories/blocks/trusted-customers-grid-desktop.stories.tsx
"use client";

import { motion } from "framer-motion";
import { ArrowRight } from "iconoir-react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import { CustomersGridAnimatedLogo } from "./customers-grid-animated-logo";
import {
  getCustomerLogoEntry,
  logoConfigsToSlots,
} from "./customers-grid-catalog";
import type { CustomerLogoId } from "./customers-grid-catalog";
import type { LocalizedCustomerGridBrand } from "./customers-grid-data";
import {
  CUSTOMER_GRID_BRANDS,
  getCustomerCaseStudyHref,
} from "./customers-grid-data";
import { getExpandDirection } from "./customers-grid-layout";
import { TrustedCustomersGridExpandButton } from "./trusted-customers-grid-expand-button";
import { TrustedCustomersGridExpandedCard } from "./trusted-customers-grid-expanded-card";
import {
  TRUSTED_CUSTOMERS_GRID,
  TRUSTED_CUSTOMERS_GRID_CARD,
  customersGridLastRowStackZIndex,
  customersGridRowBorderClass,
} from "./trusted-customers-grid-styles";
import { useCustomersGridLogoRotation } from "./use-customers-grid-logo-rotation";

const COLS = 5;

export {
  TRUSTED_CUSTOMERS_GRID,
  TRUSTED_CUSTOMERS_GRID_CARD,
} from "./trusted-customers-grid-styles";

interface TrustedCustomersGridDesktopProps {
  title: string;
  ctaLabel: string;
  ctaHref: string;
  ctaExternal?: boolean;
  logos: LocalizedCustomerGridBrand[];
  poolIds: CustomerLogoId[];
  rowCount: 2 | 3;
  animateLogos?: boolean;
  paused?: boolean;
}

function getLogoGridPosition(logoIndex: number) {
  const gridIndex = logoIndex + 1;
  return {
    gridIndex,
    row: Math.floor(gridIndex / COLS),
    col: gridIndex % COLS,
  };
}

function getCoveredGridIndex(gridIndex: number, direction: "down" | "up") {
  return direction === "down" ? gridIndex + COLS : gridIndex - COLS;
}

function getDisplayedBrandAtIndex(
  index: number,
  animateLogos: boolean,
  displaySlots: { id: CustomerLogoId }[],
  logos: LocalizedCustomerGridBrand[],
  fallback: LocalizedCustomerGridBrand
) {
  const logoId = animateLogos ? displaySlots[index]?.id : logos[index]?.id;
  if (!logoId) {
    return logos[index] ?? fallback;
  }

  return (
    CUSTOMER_GRID_BRANDS.find(({ id }) => id === logoId) ??
    logos[index] ??
    fallback
  );
}

export function TrustedCustomersGridDesktop({
  title,
  ctaLabel,
  ctaHref,
  ctaExternal = false,
  logos,
  poolIds,
  rowCount,
  animateLogos = false,
  paused = false,
}: TrustedCustomersGridDesktopProps) {
  const rotationSlots = useMemo(() => logoConfigsToSlots(logos), [logos]);

  const [expandedLogoIndex, setExpandedLogoIndex] = useState<number | null>(
    null
  );

  const displaySlots = useCustomersGridLogoRotation(
    rotationSlots,
    poolIds,
    animateLogos,
    paused || expandedLogoIndex !== null
  );

  const activeExpansion = useMemo(() => {
    if (expandedLogoIndex === null) {
      return null;
    }

    const expandedBrand = getDisplayedBrandAtIndex(
      expandedLogoIndex,
      animateLogos,
      displaySlots,
      logos,
      logos[expandedLogoIndex]!
    );
    const shortCard = expandedBrand.shortCard;
    if (!shortCard) {
      return null;
    }

    const { gridIndex, row, col } = getLogoGridPosition(expandedLogoIndex);
    const direction = getExpandDirection(row, rowCount);
    const coveredGridIndex = getCoveredGridIndex(gridIndex, direction);
    const gridRow =
      direction === "down" ? `${row + 1} / span 2` : `${row} / span 2`;

    return {
      index: expandedLogoIndex,
      brand: expandedBrand,
      shortCard,
      coveredGridIndex,
      gridRow,
      gridColumn: col + 1,
    };
  }, [animateLogos, displaySlots, expandedLogoIndex, logos, rowCount]);

  const isCovered = useCallback(
    (gridIndex: number) => activeExpansion?.coveredGridIndex === gridIndex,
    [activeExpansion]
  );

  const toggleExpanded = useCallback((logoIndex: number) => {
    setExpandedLogoIndex((current) =>
      current === logoIndex ? null : logoIndex
    );
  }, []);

  const renderCta = () => (
    <Link
      href={ctaHref}
      external={ctaExternal}
      className="text-ds-body-sm group text-grey-950 inline-flex items-center gap-1 font-medium"
      data-ph-capture-attribute-cta_label={`Trusted Customers Grid - ${ctaLabel}`}
      data-ph-capture-attribute-cta_url={ctaHref}
    >
      {ctaLabel}
      <ArrowRight
        width={16}
        height={16}
        className="shrink-0 transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  );

  const introCovered = activeExpansion?.coveredGridIndex === 0;

  return (
    <ul
      className={cn(TRUSTED_CUSTOMERS_GRID, "hidden grid-cols-5 lg:grid")}
      data-testid="trusted-customers-grid-desktop"
      style={{ gridTemplateRows: `repeat(${rowCount}, 140px)` }}
    >
      <li
        className={cn(
          TRUSTED_CUSTOMERS_GRID_CARD,
          customersGridRowBorderClass(0, rowCount),
          "h-[140px] flex-col justify-between gap-4",
          introCovered && "invisible"
        )}
        style={{ gridRow: 1, gridColumn: 1 }}
      >
        <p className="text-ds-body-lg text-grey-950 leading-[1.3] tracking-[-0.36px]">
          {title}
        </p>
        {renderCta()}
      </li>

      {logos.map((brand, index) => {
        const logoId = animateLogos ? displaySlots[index]!.id : brand.id;
        const displayedBrand = getDisplayedBrandAtIndex(
          index,
          animateLogos,
          displaySlots,
          logos,
          brand
        );
        const mediaSizeClass = animateLogos
          ? getCustomerLogoEntry(logoId).desktopMediaSize
          : getCustomerLogoEntry(brand.id).desktopMediaSize;
        const { gridIndex, row, col } = getLogoGridPosition(index);
        const shortCard = displayedBrand.shortCard;
        const showExpanded = activeExpansion?.index === index;
        const covered = isCovered(gridIndex);
        const expandable = shortCard != null;
        const gridColumn = showExpanded ? activeExpansion.gridColumn : col + 1;
        const gridRowStyle = showExpanded
          ? activeExpansion.gridRow
          : `${row + 1}`;
        const lastRowStackZ = customersGridLastRowStackZIndex(
          row,
          col,
          rowCount,
          COLS
        );

        const cellContent: ReactNode = showExpanded ? (
          <TrustedCustomersGridExpandedCard
            name={activeExpansion.brand.name}
            shortCard={activeExpansion.shortCard}
            caseStudyHref={getCustomerCaseStudyHref(activeExpansion.brand)}
          />
        ) : animateLogos ? (
          <CustomersGridAnimatedLogo logoId={logoId} variant="desktop" />
        ) : (
          <img
            src={displayedBrand.logo!.src}
            alt={displayedBrand.name}
            width={displayedBrand.logo!.width}
            height={displayedBrand.logo!.height}
          />
        );

        return (
          <motion.li
            key={brand.id}
            layout
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            data-testid={
              expandable ? `trusted-customers-grid-cell-${index}` : undefined
            }
            data-expanded={showExpanded || undefined}
            className={cn(
              TRUSTED_CUSTOMERS_GRID_CARD,
              customersGridRowBorderClass(row, rowCount),
              !showExpanded && mediaSizeClass,
              "relative items-center justify-center overflow-hidden",
              showExpanded ? "z-20 h-[279px] !p-0" : "h-[140px]",
              covered && "invisible"
            )}
            style={{
              gridRow: gridRowStyle,
              gridColumn,
              ...(lastRowStackZ !== undefined && !showExpanded
                ? { zIndex: lastRowStackZ }
                : {}),
            }}
          >
            {!covered && cellContent}
            {expandable && !covered && (
              <TrustedCustomersGridExpandButton
                expanded={showExpanded}
                onClick={() => toggleExpanded(index)}
              />
            )}
          </motion.li>
        );
      })}
    </ul>
  );
}
